// src/utils/geocode.js
const NOMINATIM_PROXY = process.env.REACT_APP_GEOCODE_PROXY;

// ─── Cache in-memory lato client (24h, evita fetch duplicate nella stessa sessione) ───
const clientCache = new Map();

function getCached(key) {
    const entry = clientCache.get(key);
    if (entry && Date.now() - entry.time < 24 * 60 * 60 * 1000) return entry.data;
    return null;
}
function setCache(key, data) {
    clientCache.set(key, { data, time: Date.now() });
}

// ─── In-flight deduplication (evita N fetch parallele per la stessa chiave) ───
const inFlight = new Map();

// ─── Utility ─────────────────────────────────────────────────────────────────
function roundCoord(lat, lng, decimals = 3) {
    const f = 10 ** decimals;
    return { lat: Math.round(lat * f) / f, lng: Math.round(lng * f) / f };
}

async function fetchJson(params) {
    const key = new URLSearchParams(params).toString();

    // 1) cache hit
    const cached = getCached(key);
    if (cached) return cached;

    // 2) richiesta identica già in volo → aspetta la stessa Promise
    if (inFlight.has(key)) return inFlight.get(key);

    const url = `${NOMINATIM_PROXY}?${key}`;

    const promise = fetch(url)
        .then(async (r) => {
            // Nominatim restituisce XML/HTML su rate-limit o errori — guard obbligatorio
            const contentType = r.headers.get("content-type") || "";
            if (!r.ok || !contentType.includes("application/json")) {
                const text = await r.text();
                console.error(`Nominatim upstream error [${r.status}]:`, text.slice(0, 300));
                throw new Error(`Nominatim ${r.status}`);
            }
            const data = await r.json();
            setCache(key, data);
            return data;
        })
        .finally(() => inFlight.delete(key));

    inFlight.set(key, promise);
    return promise;
}

// ─── 1) Geocoda una città → center + bbox ────────────────────────────────────
export async function geocodeCity({ city, state }) {
    if (!city) return null;

    const arr = await fetchJson({
        q: `${city}${state ? " " + state : ""} Italia`,
    });

    const item = Array.isArray(arr) && arr[0];
    if (!item) return null;

    const bb = item.boundingbox;
    return {
        center: {
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
        },
        bbox: bb
            ? {
                south: parseFloat(bb[0]),
                north: parseFloat(bb[1]),
                west: parseFloat(bb[2]),
                east: parseFloat(bb[3]),
            }
            : null,
    };
}

// ─── 2) Geocoding indirizzo → via + città, fallback città ────────────────────
// Usato da ProfiloOperatore.js e ProfiloUtente.js al salvataggio
export async function geocodeAddressIT({ street, city }) {
    // Se street è un display_name completo di Nominatim (es. "Via X, Città, Regione, CAP, Italia")
    // usa solo il primo segmento prima della virgola
    const cleanStreet = street && street.includes(',')
        ? street.split(',')[0].trim()
        : street;

    if (cleanStreet && cleanStreet.length >= 4 && city) {
        try {
            const arr = await fetchJson({ q: `${cleanStreet} ${city} Italia` });
            if (Array.isArray(arr) && arr.length > 0) {
                return roundCoord(parseFloat(arr[0].lat), parseFloat(arr[0].lon), 3);
            }
        } catch (_) { }
    }

    if (city) {
        const arr = await fetchJson({ q: `${city} Italia` });
        if (Array.isArray(arr) && arr.length > 0) {
            return roundCoord(parseFloat(arr[0].lat), parseFloat(arr[0].lon), 3);
        }
    }

    throw new Error("Impossibile geocodificare l'indirizzo");
}

// ─── 3) Geocoding CAP ────────────────────────────────────────────────────────
// Usato da ProfiloOperatore.js e ProfiloUtente.js al salvataggio
export async function geocodePostalIT({ postalcode, city }) {
    const arr = await fetchJson({
        q: `${postalcode}${city ? " " + city : ""} Italia`,
    });

    const item = Array.isArray(arr) && arr[0];
    if (!item) throw new Error("CAP non trovato in Italia");

    return roundCoord(parseFloat(item.lat), parseFloat(item.lon), 3);
}